import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { BusinessOutlined as BusinessIcon } from '@mui/icons-material';
import { fetchDepartments, fetchUserAccess, updateUserAccess } from './api';
import { DepartmentRead, User } from './types';
import { CustomCheckbox } from '../../components/ui/CustomCheckbox';
import { AppPagination } from '../../components/ui/AppPagination';

const PAGE_SIZE = 10;

const AccessSettingsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const user = location.state?.user as User | undefined;

  const [departments, setDepartments] = useState<DepartmentRead[]>([]);
  const [hasFullAccess, setHasFullAccess] = useState(false);
  const [selectedDeps, setSelectedDeps] = useState<number[]>([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [page, setPage] = useState(1);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [deps, access] = await Promise.all([
          fetchDepartments(),
          fetchUserAccess(Number(id))
        ]);

        setDepartments(deps);
        setHasFullAccess(access.has_full_access);
        setSelectedDeps(access.department_ids || []);
      } catch (error) {
        console.error("Ошибка загрузки данных", error);
        alert("Не удалось загрузить настройки доступа");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  const toggleDep = (depId: number) => {
    setSelectedDeps(prev =>
      prev.includes(depId) ? prev.filter(d => d !== depId) : [...prev, depId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateUserAccess(Number(id), {
        has_full_access: hasFullAccess,
        department_ids: hasFullAccess ? [] : selectedDeps
      });
      navigate('/access');
    } catch (error) {
      alert("Ошибка при сохранении прав доступа");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center w-full h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const displayName = user ? (user.full_name || user.email) : `ID пользователя: ${id}`;

  const totalItems = departments.length;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const pagedDepartments = departments.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="w-full bg-white pb-10">
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8 flex flex-col min-h-[calc(100vh-6rem)]">

        {/* Header - Кнопка "Назад" слева сверху, в запрошенном стиле */}
        <div className="flex flex-col items-start gap-4 mb-8 shrink-0">
          <Link
            to="/access"
            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 group"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Назад к списку
          </Link>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            Настройка доступа: <span className="text-[#1E61AF]">{displayName}</span>
          </h1>
        </div>

        {/* Контейнер на всю ширину страницы */}
        <div className="flex flex-col gap-6 w-full">

          {/* Глобальные права (без карточки, просто плашка) */}
          <div
            onClick={() => setHasFullAccess(!hasFullAccess)}
            className={`cursor-pointer flex items-start gap-4 p-5 rounded-xl border transition-all select-none w-full ${
              hasFullAccess
                ? 'bg-blue-50 border-blue-400 shadow-sm'
                : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-sm'
            }`}
          >
            <div className="pointer-events-none mt-0.5">
              <CustomCheckbox
                id="full-access-checkbox"
                checked={hasFullAccess}
                onChange={() => {}}
              />
            </div>
            <div>
              <p className={`font-bold text-base ${hasFullAccess ? 'text-[#1E61AF]' : 'text-gray-900'}`}>
                Полный доступ (Администратор заявок)
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Если включено, пользователь будет видеть заявки абсолютно всех отделов. Выбор конкретных отделов ниже будет проигнорирован.
              </p>
            </div>
          </div>

          {/* Карточка со списком отделов */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col w-full">
            <div className="p-6 border-b border-gray-100 bg-gray-50/50">
              <h3 className="text-lg font-semibold text-gray-900">Доступные отделы</h3>
              <p className="text-sm text-gray-500 mt-1">Выберите отделы, заявки которых сможет видеть пользователь</p>
            </div>

            <div className={`p-6 transition-opacity duration-300 ${hasFullAccess ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>

              {departments.length === 0 ? (
                <p className="p-6 text-sm text-gray-500 text-center border border-gray-200 rounded-lg bg-gray-50">
                  Отделы не найдены
                </p>
              ) : (
                <div className="space-y-3">
                  {pagedDepartments.map(dep => {
                    const isSelected = selectedDeps.includes(dep.id);
                    return (
                      <div
                        key={dep.id}
                        onClick={() => toggleDep(dep.id)}
                        className={`cursor-pointer flex items-center w-full text-left rounded-lg border p-4 shadow-sm transition-all select-none ${
                          isSelected
                            ? 'bg-blue-50 border-blue-400 shadow-md'
                            : 'bg-white border-gray-200 hover:border-blue-400 hover:shadow-md'
                        }`}
                      >
                        <div className="flex items-center gap-4 w-full">
                          {/* Отключаем клики внутри чекбокса, чтобы весь клик обрабатывался на уровне родителя */}
                          <div className="pointer-events-none">
                            <CustomCheckbox
                              id={`dep-checkbox-${dep.id}`}
                              checked={isSelected}
                              onChange={() => {}}
                            />
                          </div>

                          <BusinessIcon className={isSelected ? 'text-[#1E61AF]' : 'text-gray-400'} />

                          <div className="flex flex-col">
                            <span className={`font-medium ${isSelected ? 'text-[#1E61AF]' : 'text-gray-800'}`}>
                              {dep.name}
                            </span>
                            <span className="text-xs text-gray-500">
                              Папка: {dep.subfolder_name || '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Пагинация */}
                  {totalPages > 1 && (
                    <div className="pt-4 flex justify-center">
                      <AppPagination
                        page={page}
                        totalItems={totalItems}
                        pageSize={PAGE_SIZE}
                        onChange={setPage}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Подсказка при пустом доступе */}
              {!hasFullAccess && selectedDeps.length === 0 && (
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800 font-medium">Доступ ограничен</p>
                  <p className="text-xs text-blue-700 mt-1">
                    Пользователь не увидит ни одной заявки. Это равносильно временной блокировке просмотра.
                  </p>
                </div>
              )}
            </div>

            {/* Подвал карточки с кнопкой сохранения */}
            <div className="flex justify-end gap-3 p-4 bg-gray-50 border-t border-gray-200">
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center justify-center rounded-md bg-[#1E61AF] px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-800 transition-colors disabled:opacity-50"
              >
                {saving ? 'Сохранение...' : 'Сохранить настройки'}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AccessSettingsPage;